/**
 * FIND/REPLACE engine (Faz 8.2, OCS find_replace.rs parity).
 *
 * Scan surfaces: TEXT/MTEXT content, ATTDEF default values, INSERT instance
 * attribute values, and block-definition children (reached through INSERTs,
 * with a visited set guarding nested definitions). Dimension text overrides,
 * tables and xrefs are NOT scanned (OCS scope). Matching is case-insensitive
 * only — no whole-word/wildcard/regex modes.
 *
 * Replacements run as ONE undo step per invocation ("FIND/REPLACE" for a
 * single next, "FIND/REPLACE ALL" for all); zero-change runs push no history.
 */

import { useAppStore, type Entity } from '../../state/store';
import { useBlockStore } from '../../state/commandAuxStores';

/** One hit. `target` says which surface holds the text and how to write it. */
export interface FindMatch {
    /** Status-line label ("{i+1} of {n} — {label}"). */
    label: string;
    /** World position for camera centering (insertion point / text position). */
    at: [number, number, number] | null;
    /** The entity id to select when navigating here (null = definition hit). */
    focusId: string | null;
    target:
        | { kind: 'entity'; id: string; field: 'content' | 'defaultValue' }
        | { kind: 'definition'; blockName: string; index: number; field: 'content' | 'defaultValue' }
        | { kind: 'insertAttribute'; insertId: string; tag: string };
    /** Current text of the matched surface. */
    text: string;
}

/** Case-insensitive global replace with a literal needle. */
function replaceAllInsensitive(text: string, needle: string, replaceWith: string): string {
    if (!needle) return text;
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(new RegExp(escaped, 'gi'), () => replaceWith);
}

/** True when the entity's layer is locked (locked layers are skipped). */
function isOnLockedLayer(e: Entity): boolean {
    const layer = useAppStore.getState().layers[e.layer];
    return !!layer?.locked;
}

/** The searchable text fields of one entity bag (OCS searchable fields). */
function entityTextFields(e: Entity): Array<{ field: 'content' | 'defaultValue'; text: string }> {
    const d = e.data as Record<string, any>;
    if (e.type === 'text' || e.type === 'mtext') {
        return [{ field: 'content', text: String(d.content ?? '') }];
    }
    if (e.type === 'attdef') {
        return [{ field: 'defaultValue', text: String(d.defaultValue ?? '') }];
    }
    return [];
}

/**
 * Collect every match of `needle` (case-insensitive) across the drawing and
 * the block definitions reachable from INSERTs (visited set guards nesting).
 */
export function collectFindMatches(needle: string): FindMatch[] {
    const trimmed = needle.trim();
    if (!trimmed) return [];
    const lower = trimmed.toLowerCase();
    const app = useAppStore.getState();
    const blockStore = useBlockStore.getState();
    const out: FindMatch[] = [];
    const worldAt = (p: unknown): [number, number, number] | null =>
        Array.isArray(p) && p.length >= 2 ? [p[0], p[1], p[2] ?? 0] : null;

    // 1. Direct drawing entities (locked layers rejected, OCS parity).
    const drawing = Object.values(app.entities).filter((e) => !isOnLockedLayer(e));
    for (const e of drawing) {
        const at = worldAt((e.data as Record<string, unknown>).position);
        for (const f of entityTextFields(e)) {
            if (f.text.toLowerCase().includes(lower)) {
                out.push({
                    label: `${e.type} "${f.text}"`,
                    at, focusId: e.id, text: f.text,
                    target: { kind: 'entity', id: e.id, field: f.field },
                });
            }
        }
    }

    // 2. INSERT instance attribute values + the definitions they reference.
    const defs = blockStore.defs;
    const visited = new Set<string>();
    const queue: string[] = [];
    for (const e of drawing) {
        if (e.type !== 'insert') continue;
        const name = String((e.data as Record<string, unknown>).blockName ?? '');
        if (!name) continue;
        const values = (e.data as Record<string, unknown>).attributeValues as Record<string, string> | undefined;
        if (values) {
            for (const [tag, value] of Object.entries(values)) {
                if (String(value).toLowerCase().includes(lower)) {
                    out.push({
                        label: `insert "${name}" attribute "${tag}" = "${value}"`,
                        at: worldAt((e.data as Record<string, unknown>).position),
                        focusId: e.id,
                        target: { kind: 'insertAttribute', insertId: e.id, tag },
                        text: String(value),
                    });
                }
            }
        }
        if (defs[name]) queue.push(name);
    }
    // 3. Definition expansion: block children + nested definitions (the
    //    visited set keeps self/nested references from looping).
    while (queue.length > 0) {
        const blockName = queue.shift()!;
        if (visited.has(blockName)) continue;
        visited.add(blockName);
        const def = defs[blockName];
        if (!def) continue;
        def.entities.forEach((child, index) => {
            const at = worldAt((child.data as Record<string, unknown>).position);
            for (const f of entityTextFields(child as Entity)) {
                if (f.text.toLowerCase().includes(lower)) {
                    out.push({
                        label: `block "${blockName}" child #${index} ${f.field}`,
                        at, focusId: null, text: f.text,
                        target: { kind: 'definition', blockName, index, field: f.field },
                    });
                }
            }
            if (child.type === 'insert') {
                const nested = String((child.data as Record<string, unknown>).blockName ?? '');
                if (nested && defs[nested] && !visited.has(nested)) queue.push(nested);
            }
        });
    }
    return out;
}

/**
 * Apply the replacement to a set of matches as ONE undo step. Definition
 * hits rewrite the block definition (snapshot + custom history entry);
 * entity/attribute hits ride the standard entity snapshots. Returns the
 * number of surfaces actually changed.
 */
export function applyReplacements(matches: FindMatch[], needle: string, replaceWith: string, historyLabel: string): number {
    if (!needle) return 0;
    const app = useAppStore.getState();
    const blockStore = useBlockStore.getState();
    const entityIds = new Set<string>();
    const entityFields = new Map<string, Set<'content' | 'defaultValue'>>();
    const attrTargets = new Map<string, Set<string>>();
    const defTargets = new Map<string, Set<number>>();
    let changed = 0;

    for (const m of matches) {
        const next = replaceAllInsensitive(m.text, needle, replaceWith);
        if (next === m.text) continue;
        changed++;
        if (m.target.kind === 'entity') {
            entityIds.add(m.target.id);
            const fields = entityFields.get(m.target.id) ?? new Set();
            fields.add(m.target.field);
            entityFields.set(m.target.id, fields);
        } else if (m.target.kind === 'insertAttribute') {
            const tags = attrTargets.get(m.target.insertId) ?? new Set();
            tags.add(m.target.tag);
            attrTargets.set(m.target.insertId, tags);
        } else {
            const idxs = defTargets.get(m.target.blockName) ?? new Set();
            idxs.add(m.target.index);
            defTargets.set(m.target.blockName, idxs);
        }
    }
    if (changed === 0) return 0;

    // Block-definition snapshots (before/after) for the combined undo step.
    const defBefore = new Map<string, Entity[]>();
    const defAfter = new Map<string, Entity[]>();
    defTargets.forEach((idxs, name) => {
        const def = blockStore.defs[name];
        if (!def) return;
        defBefore.set(name, structuredClone(def.entities));
        defAfter.set(name, (def.entities as Entity[]).map((child, i) => {
            if (!idxs.has(i)) return child;
            const field = child.type === 'attdef' ? 'defaultValue' : 'content';
            const next = replaceAllInsensitive(String((child.data as Record<string, any>)[field] ?? ''), needle, replaceWith);
            return { ...child, data: { ...child.data, [field]: next } };
        }));
    });

    // Entity data snapshots (content/defaultValue surfaces).
    const entBefore = new Map<string, Record<string, any>>();
    const entAfter = new Map<string, Record<string, any>>();
    entityIds.forEach((id) => {
        const e = app.entities[id];
        if (!e) return;
        const before = structuredClone(e.data);
        const after = structuredClone(e.data);
        (entityFields.get(id) ?? new Set()).forEach((field) => {
            after[field] = replaceAllInsensitive(String(after[field] ?? ''), needle, replaceWith);
        });
        entBefore.set(id, before);
        entAfter.set(id, after);
    });

    // Attribute instance snapshots.
    const attrBefore = new Map<string, Record<string, string>>();
    const attrAfter = new Map<string, Record<string, string>>();
    attrTargets.forEach((tags, id) => {
        const e = app.entities[id];
        if (!e) return;
        const before = { ...((e.data.attributeValues as Record<string, string>) ?? {}) };
        const after = { ...before };
        tags.forEach((tag) => { after[tag] = replaceAllInsensitive(String(after[tag] ?? ''), needle, replaceWith); });
        attrBefore.set(id, before);
        attrAfter.set(id, after);
    });

    const applyDefs = (side: Map<string, Entity[]>) => {
        const bs = useBlockStore.getState();
        side.forEach((entities, name) => {
            const def = bs.defs[name];
            if (!def) return;
            bs.define({ ...def, entities: structuredClone(entities) });
        });
    };
    const applyEntities = (dataMap: Map<string, Record<string, any>>, attrMap: Map<string, Record<string, string>>) => {
        const a = useAppStore.getState();
        dataMap.forEach((d, id) => { if (a.entities[id]) a.updateEntity(id, { data: d }); });
        attrMap.forEach((values, id) => {
            if (a.entities[id]) a.updateEntity(id, { data: { ...a.entities[id].data, attributeValues: values } });
        });
    };

    // Write the "after" state; definitions first (block store), then entities.
    applyDefs(defAfter);
    applyEntities(entAfter, attrAfter);

    // ONE combined history entry (OCS "FIND/REPLACE" snapshot semantics).
    app.pushHistory({
        type: 'cmd.modify',
        timestamp: Date.now(),
        data: { count: changed, label: historyLabel },
        undo: () => {
            applyDefs(defBefore);
            applyEntities(entBefore, attrBefore);
        },
        redo: () => {
            applyDefs(defAfter);
            applyEntities(entAfter, attrAfter);
        },
    });
    return changed;
}

/** Select + center the camera on a match (OCS find_replace_next flow). */
export function focusFindMatch(match: FindMatch): void {
    const app = useAppStore.getState();
    if (match.focusId && app.entities[match.focusId]) {
        app.select([match.focusId]);
        window.dispatchEvent(new CustomEvent('yap:zoom-to-selection'));
    }
}
